/**
 * @file `SettingsPanel` — root component of the `/settings` route
 *       (umbrella #883, part #894).
 * @module @labcraft/admin/ui/settings/settings-panel
 *
 * One nav tab, two modes behind a head-segmented `Settings | Secrets`
 * toggle (the nav stays on "Settings" in both). A left daemon-filter
 * rail scopes either view. **Settings** are operator-controlled runtime
 * config — type-aware widgets, Save/Reset with a dirty indicator, and a
 * lazy per-key History/Revert. **Secrets** are write-only: a masked
 * value + version + last-rotated (or "never rotated"), a "Needs
 * rotation" rail filter, and a Rotate modal whose entered value never
 * crosses the wire back.
 *
 * Schema/values/secret metadata flow through the TanStack hook layer;
 * the SECRETS Needs-you items + Sources tile are emitted by the
 * Dashboard's `DashboardSources.secrets` (the contract reference impl).
 */

import { useMemo, useState, type JSX } from 'react'
import type { SettingDefinition } from '@labcraft/postgres/shared'
import {
  useSecretsList,
  useSettingsSchema,
  useSettingsValues,
} from '../hooks/index.ts'
import { SettingRow } from './setting_row.tsx'
import { SecretRow } from './secret_row.tsx'
import { SettingsView } from './view.ts'

/** Which sub-view the segmented toggle selects. */
type Mode = 'settings' | 'secrets'

/** Render the Settings | Secrets route. */
export function SettingsPanel(): JSX.Element {
  const [mode, setMode] = useState<Mode>('settings')
  const [daemon, setDaemon] = useState<string>('all')
  const [onlyStale, setOnlyStale] = useState(false)

  const switchMode = (next: Mode): void => {
    setMode(next)
    setDaemon('all')
    setOnlyStale(false)
  }

  return (
    <div className="panel-split">
      {mode === 'settings' ? (
        <SettingsMode
          daemon={daemon}
          onDaemon={setDaemon}
          mode={mode}
          onMode={switchMode}
        />
      ) : (
        <SecretsMode
          daemon={daemon}
          onDaemon={setDaemon}
          onlyStale={onlyStale}
          onOnlyStale={setOnlyStale}
          mode={mode}
          onMode={switchMode}
        />
      )}
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Head segmented toggle
////////////////////////////////////////////////////////////////////////////////

/** The `Settings | Secrets` segmented control rendered in the head. */
function ModeToggle(props: {
  mode: Mode
  onMode: (m: Mode) => void
}): JSX.Element {
  return (
    <div className="seg" role="group" aria-label="Settings or Secrets">
      {(['settings', 'secrets'] as const).map(m => (
        <button
          key={m}
          type="button"
          className="seg__btn"
          aria-pressed={props.mode === m}
          onClick={() => props.onMode(m)}>
          {m === 'settings' ? 'Settings' : 'Secrets'}
        </button>
      ))}
    </div>
  )
}

/** Left daemon-filter rail shared by both sub-views. */
function DaemonRail(props: {
  items: ReadonlyArray<{ id: string; label: string; count?: number }>
  selected: string
  onSelect: (id: string) => void
  extra?: JSX.Element
}): JSX.Element {
  return (
    <nav className="panel-rail" aria-label="Daemon filter">
      <div className="panel-rail__h">Daemons</div>
      {props.items.map(it => (
        <button
          key={it.id}
          className="panel-rail__item"
          aria-current={props.selected === it.id ? 'page' : undefined}
          onClick={() => props.onSelect(it.id)}>
          <span>{it.label}</span>
          {it.count !== undefined && (
            <span className="panel-rail__count">{it.count}</span>
          )}
        </button>
      ))}
      {props.extra}
    </nav>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Settings sub-view
////////////////////////////////////////////////////////////////////////////////

/** The runtime-config sub-view. */
function SettingsMode(props: {
  daemon: string
  onDaemon: (id: string) => void
  mode: Mode
  onMode: (m: Mode) => void
}): JSX.Element {
  const schema = useSettingsSchema()
  const values = useSettingsValues()

  const loading = schema.isPending || values.isPending
  const errorMessage = schema.isError
    ? schema.error.message || 'Load failed.'
    : values.isError
      ? values.error.message || 'Load failed.'
      : null

  const allGroups = useMemo(() => schema.data?.daemons ?? [], [schema.data])
  const groups = useMemo(
    () => SettingsView.filterGroups(allGroups, props.daemon),
    [allGroups, props.daemon],
  )
  const valuesMap = values.data?.values ?? {}
  const valuesFor = (daemon: string): Record<string, unknown> =>
    valuesMap[daemon] ?? {}

  const railItems = [
    { id: 'all', label: 'All' },
    ...allGroups.map(g => ({ id: g.id, label: g.label, count: g.keys.length })),
  ]

  return (
    <>
      <DaemonRail
        items={railItems}
        selected={props.daemon}
        onSelect={props.onDaemon}
      />
      <div className="panel panel--no-subbar">
        <header className="panel-head">
          <div>
            <div className="panel-head__title-row">
              <h1 className="panel-head__title">Settings</h1>
            </div>
            <p className="panel-head__sub">
              // operator-controlled runtime config; writes propagate via
              postgres LISTEN/NOTIFY.
            </p>
          </div>
          <ModeToggle mode={props.mode} onMode={props.onMode} />
        </header>
        <div className="panel-body">
          {loading && <div className="cfg-empty">// loading…</div>}
          {errorMessage !== null && (
            <div className="panel-banner panel-banner--error">
              <b>Could not load settings.</b> {errorMessage}
            </div>
          )}
          {!loading &&
            errorMessage === null &&
            groups.map(group => (
              <section className="cfg-group" key={group.id}>
                <header className="cfg-group__head">
                  <h2 className="cfg-group__title">{group.label}</h2>
                  <span className="cfg-group__sub">{group.id}</span>
                </header>
                <ul className="cfg-list">
                  {group.keys.map((def: SettingDefinition) => (
                    <SettingRow
                      key={def.key}
                      daemon={group.id}
                      def={def}
                      currentValue={valuesFor(group.id)[def.key]}
                    />
                  ))}
                </ul>
              </section>
            ))}
        </div>
      </div>
    </>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Secrets sub-view
////////////////////////////////////////////////////////////////////////////////

/** The write-only secrets sub-view. */
function SecretsMode(props: {
  daemon: string
  onDaemon: (id: string) => void
  onlyStale: boolean
  onOnlyStale: (v: boolean) => void
  mode: Mode
  onMode: (m: Mode) => void
}): JSX.Element {
  const list = useSecretsList()
  const now = Date.now()
  const entries = useMemo(() => list.data?.entries ?? [], [list.data])
  const groups = useMemo(
    () =>
      SettingsView.groupSecrets(entries, props.daemon, props.onlyStale, now),
    [entries, props.daemon, props.onlyStale, now],
  )
  const stale = SettingsView.staleCount(entries, now)
  const errorMessage = list.isError
    ? list.error.message || 'Load failed.'
    : null

  const railItems = [
    { id: 'all', label: 'All' },
    ...SettingsView.secretDaemons(entries).map(d => ({ id: d, label: d })),
  ]

  return (
    <>
      <DaemonRail
        items={railItems}
        selected={props.daemon}
        onSelect={props.onDaemon}
        extra={
          <>
            <div className="panel-rail__h" style={{ marginTop: 8 }}>
              Rotation
            </div>
            <button
              className="panel-rail__item"
              aria-current={props.onlyStale ? 'page' : undefined}
              onClick={() => props.onOnlyStale(!props.onlyStale)}>
              <span>Needs rotation</span>
              <span className="panel-rail__count">{stale}</span>
            </button>
          </>
        }
      />
      <div className="panel panel--no-subbar">
        <header className="panel-head">
          <div>
            <div className="panel-head__title-row">
              <h1 className="panel-head__title">Settings</h1>
            </div>
            <p className="panel-head__sub">
              // write-only secrets — values never displayed, returned, or
              logged.
            </p>
          </div>
          <ModeToggle mode={props.mode} onMode={props.onMode} />
        </header>
        <div className="panel-body">
          {list.isPending && <div className="cfg-empty">// loading…</div>}
          {errorMessage !== null && (
            <div className="panel-banner panel-banner--error">
              <b>Could not load secrets.</b> {errorMessage}
            </div>
          )}
          {!list.isPending && errorMessage === null && groups.length === 0 && (
            <div className="cfg-empty">// no secrets match this filter.</div>
          )}
          {groups.map(group => (
            <section className="cfg-group" key={group.daemon}>
              <header className="cfg-group__head">
                <h2 className="cfg-group__title">{group.daemon}</h2>
                <span className="cfg-group__sub">
                  {group.entries.length} declared
                </span>
              </header>
              <ul className="cfg-list">
                {group.entries.map(entry => (
                  <SecretRow
                    key={entry.definition.path}
                    entry={entry}
                    now={now}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
